import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FiUsers, FiShield, FiTrendingUp, FiHeart } from "react-icons/fi";

gsap.registerPlugin(ScrollTrigger);

const AgeGradeMeaning = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const visualRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 70%",
        toggleActions: "play none none none",
      },
    });

    tl.from(visualRef.current, {
      opacity: 0,
      x: -100,
      duration: 1.2,
      ease: "power3.out",
    }).from(
      section.querySelectorAll(".content-card"),
      {
        opacity: 0,
        y: 50,
        stagger: 0.2,
        duration: 0.8,
        ease: "power2.out",
      },
      "-=0.8"
    );

    return () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  const points = [
    {
      icon: <FiUsers className="text-accent h-6 w-6" />,
      title: "Unity & Brotherhood",
      text: "Fostering lifelong bonds and a powerful sense of belonging among peers.",
    },
    {
      icon: <FiShield className="text-accent h-6 w-6" />,
      title: "Social Responsibility",
      text: "Organizing community projects, ensuring security, and upholding our values.",
    },
    {
      icon: <FiTrendingUp className="text-accent h-6 w-6" />,
      title: "Leadership Grooming",
      text: "A structured path for young members to learn, lead, and prepare for future roles.",
    },
    {
      icon: <FiHeart className="text-accent h-6 w-6" />,
      title: "Cultural Continuity",
      text: "Passing down traditions, stories, and the wisdom of our ancestors.",
    },
  ];

  return (
    <section ref={sectionRef} className="bg-white py-20 md:py-28">
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-6 md:grid-cols-2 md:gap-16">
        {/* Visual Element */}
        <div
          ref={visualRef}
          className="flex h-80 min-h-[24rem] items-center justify-center overflow-hidden rounded-2xl bg-stone-100 p-8 md:h-full"
        >
          <img
            src="https://source.unsplash.com/random/600x800?african,pattern,art"
            alt="Cultural Symbol"
            className="h-full w-full rounded-lg object-cover shadow-lg"
          />
        </div>

        {/* Content */}
        <div>
          <div className="content-card bg-transparent p-0">
            <h2 className="font-serif text-3xl font-semibold leading-snug text-nature-green-dark md:text-4xl">
              The Heartbeat of Our Community
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-gray-700">
              Age grades are more than just groups; they are the fundamental
              structure upon which our society is built. They are families,
              workforces, and the guardians of our culture, providing a
              framework for life from youth to elderhood.
            </p>
          </div>

          <div className="mt-10 space-y-6">
            {points.map((point, index) => (
              <div
                key={index}
                className="content-card flex items-start gap-4 rounded-xl border border-stone-200/60 bg-stone-50 p-5"
              >
                <div className="rounded-full bg-complementary/20 p-3">
                  {point.icon}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">
                    {point.title}
                  </h3>
                  <p className="text-gray-600">{point.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AgeGradeMeaning;
